/**
 * Event WebSocket Event Manager
 * Handles event subscription and emission for WebPush events
 */

import type { EventWsEvent, EventWsEventHandler, PsnFilterConfig, WebPushMessage } from './types';

/**
 * Default PSN extraction function
 * Extracts PSN from WebPush message text
 * @param message - Message text to extract PSN from
 * @returns Extracted PSN or null if not found
 */
function defaultExtractPsn(message: string): string | null {
  // Common PSN patterns in WebPush messages
  const patterns = [
    /PSN[:\s]*([A-Z0-9]{11})/i,           // "PSN: ABC12345678" or "PSN ABC12345678"
    /Device[:\s]*([A-Z0-9]{11})/i,        // "Device: ABC12345678"
    /\[([A-Z0-9]{11})\]/,                 // "[ABC12345678]"
    /^([A-Z0-9]{11})/,                    // Message starts with PSN
    /([A-Z0-9]{11})/                      // Any 11-character alphanumeric string
  ];
  
  for (const pattern of patterns) {
    const match = message.match(pattern);
    if (match && match[1]) {
      return match[1].toUpperCase();
    }
  }
  
  return null;
}

/**
 * Event Manager class
 * Manages event subscriptions and notifications for WebPush events
 */
export class EventWebSocketEventManager {
  private eventHandlers: Partial<Record<EventWsEvent, EventWsEventHandler[]>> = {};
  private psnFilter: PsnFilterConfig;

  /**
   * Create Event Manager with PSN filtering support
   * @param psnFilter - PSN filter configuration
   */
  constructor(psnFilter?: Partial<PsnFilterConfig>) {
    this.psnFilter = {
      enableFiltering: false,
      allowedPsns: new Set<string>(),
      extractPsnFromMessage: defaultExtractPsn,
      ...psnFilter
    };
  }

  /**
   * Add event listener
   * @param event - Event type to listen for
   * @param handler - Event handler function
   */
  on<T = any>(event: EventWsEvent, handler: EventWsEventHandler<T>): void {
    if (!this.eventHandlers[event]) {
      this.eventHandlers[event] = [];
    }
    this.eventHandlers[event]!.push(handler as EventWsEventHandler);
  }

  /**
   * Remove specific event listener
   * @param event - Event type
   * @param handler - Event handler to remove
   */
  off(event: EventWsEvent, handler: EventWsEventHandler): void {
    const handlers = this.eventHandlers[event];
    if (handlers) {
      const index = handlers.indexOf(handler);
      if (index !== -1) {
        handlers.splice(index, 1);
      }
    }
  }

  /**
   * Remove all listeners for a specific event
   * @param event - Event type (optional - removes all if not specified)
   */
  removeAllListeners(event?: EventWsEvent): void {
    if (event) {
      this.eventHandlers[event] = [];
    } else {
      this.eventHandlers = {};
    }
  }

  /**
   * Emit event to all registered handlers
   * @param event - Event type
   * @param data - Event data
   */
  emit<T = any>(event: EventWsEvent, data?: T): void {
    // Apply PSN filtering for webpush events
    if (event === 'webpush' && this.psnFilter.enableFiltering && data) {
      const message = data as unknown as WebPushMessage;
      const extractedPsn = this.psnFilter.extractPsnFromMessage(message.message);
      
      if (extractedPsn && !this.psnFilter.allowedPsns.has(extractedPsn)) {
        return; // Filter out this message
      }
    }
    
    const handlers = this.eventHandlers[event] || [];
    
    if (handlers.length === 0) {
      return;
    }
    
    handlers.forEach((handler, index) => {
      try {
        handler(data);
      } catch (error) {
        console.error(`Error in event handler for '${event}':`, error);
      }
    });
  }

  /**
   * Set PSN filters for event filtering
   * @param psns - Array of PSNs to filter by
   */
  setPsnFilters(psns: string[]): void {
    this.psnFilter.allowedPsns = new Set(psns.map(psn => psn.toUpperCase()));
    this.psnFilter.enableFiltering = psns.length > 0;
  }

  /**
   * Add PSN to filter list
   * @param psn - PSN to add to filter
   */
  addPsnFilter(psn: string): void {
    this.psnFilter.allowedPsns.add(psn.toUpperCase());
    this.psnFilter.enableFiltering = this.psnFilter.allowedPsns.size > 0;
  }

  /**
   * Remove PSN from filter list
   * @param psn - PSN to remove from filter
   */
  removePsnFilter(psn: string): void {
    this.psnFilter.allowedPsns.delete(psn.toUpperCase());
    this.psnFilter.enableFiltering = this.psnFilter.allowedPsns.size > 0;
  }

  /**
   * Clear all PSN filters
   */
  clearPsnFilters(): void {
    this.psnFilter.allowedPsns.clear();
    this.psnFilter.enableFiltering = false;
  }

  /**
   * Get current PSN filters
   * @returns Array of current PSN filters
   */
  getPsnFilters(): string[] {
    return Array.from(this.psnFilter.allowedPsns);
  }

  /**
   * Check if PSN filtering is enabled
   * @returns True if PSN filtering is active
   */
  isPsnFilteringEnabled(): boolean {
    return this.psnFilter.enableFiltering;
  }

  /**
   * Set custom PSN extraction function
   * @param extractorFn - Custom PSN extraction function
   */
  setPsnExtractor(extractorFn: (message: string) => string | null): void {
    this.psnFilter.extractPsnFromMessage = extractorFn;
  }

  /**
   * Test PSN extraction on a message
   * @param message - Message to test PSN extraction on
   * @returns Extracted PSN or null
   */
  testPsnExtraction(message: string): string | null {
    return this.psnFilter.extractPsnFromMessage(message);
  }

  /**
   * Get number of handlers for an event
   * @param event - Event type
   * @returns Number of registered handlers
   */
  getHandlerCount(event: EventWsEvent): number {
    return this.eventHandlers[event]?.length || 0;
  }

  /**
   * Get all registered events
   * @returns Array of event types that have handlers
   */
  getRegisteredEvents(): EventWsEvent[] {
    return Object.keys(this.eventHandlers) as EventWsEvent[];
  }

  /**
   * Check if event has any handlers
   * @param event - Event type
   * @returns True if event has handlers
   */
  hasHandlers(event: EventWsEvent): boolean {
    return this.getHandlerCount(event) > 0;
  }

  /**
   * Get total number of handlers across all events
   * @returns Total handler count
   */
  getTotalHandlerCount(): number {
    return Object.values(this.eventHandlers).reduce(
      (total, handlers) => total + (handlers?.length || 0), 
      0
    );
  }

  /**
   * Clear all event handlers
   */
  destroy(): void {
    this.eventHandlers = {};
  }
}